import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { entraConfigSchema, type EntraConfigInput, type EntraRoleMapping, type SecuritySettings } from '@orion/contracts';
import { Button, Input, Select, Switch } from '@orion/ui';
import { PageHeader } from '../../../app/shell.js';
import { ApiError } from '../../../lib/api-client.js';
import { useRolesQuery } from '../roles/api.js';
import { useSettingsQuery, useUpdateSettings } from '../settings/api.js';
import {
  useAddDomain,
  useDomainsQuery,
  useEntraConfigQuery,
  useRemoveDomain,
  useSetRequireSso,
  useSsoHealthQuery,
  useTestEntraConnection,
  useUpdateEntraConfig,
  useVerifyDomain,
} from './api.js';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return formatDate(iso);
}

function maskMiddle(value: string): string {
  if (value.length <= 13) return value;
  return `${value.slice(0, 8)}-••••-••••-••••-${value.slice(-12)}`;
}

/** docs section 5 "Administrator Control" — Authentication & SSO
 * (design/orion-crm-design/rendered-html/AdminSSO.html). Real Microsoft
 * Entra ID SSO already exists end to end (OIDC/PKCE, JIT provisioning,
 * account linking) — this screen fills in what was genuinely missing:
 * domain-based SSO enforcement, group → role mapping, and a connection
 * health/test surface, rather than just editing the 6 fields the old form
 * covered. */
export function SsoSettingsPage() {
  const { data, isLoading } = useEntraConfigQuery();
  const { data: roles } = useRolesQuery();
  const { data: health } = useSsoHealthQuery();
  const { data: domains } = useDomainsQuery();
  const { data: security } = useSettingsQuery<SecuritySettings>('security');
  const update = useUpdateEntraConfig();
  const testConnection = useTestEntraConnection();
  const updateSecurity = useUpdateSettings<SecuritySettings>('security');
  const addDomain = useAddDomain();
  const verifyDomain = useVerifyDomain();
  const removeDomain = useRemoveDomain();
  const setRequireSso = useSetRequireSso();

  const [roleMappings, setRoleMappings] = useState<EntraRoleMapping[]>([]);
  const [showTenantId, setShowTenantId] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [passwordEnabled, setPasswordEnabled] = useState(true);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting, isDirty },
  } = useForm<EntraConfigInput>({ resolver: zodResolver(entraConfigSchema) });

  useEffect(() => {
    if (data) {
      reset({ ...data, entraTenantId: data.entraTenantId ?? '', clientId: data.clientId ?? '', defaultRoleId: data.defaultRoleId ?? '' });
      setRoleMappings(data.roleMappings);
    }
  }, [data, reset]);
  useEffect(() => {
    if (security) setPasswordEnabled(security.passwordSignInEnabled);
  }, [security]);

  const roleMappingsDirty = JSON.stringify(data?.roleMappings ?? []) !== JSON.stringify(roleMappings);
  const passwordDirty = security ? passwordEnabled !== security.passwordSignInEnabled : false;
  const dirty = isDirty || roleMappingsDirty || passwordDirty;

  const errorMessage = update.error instanceof ApiError ? (update.error.problem.detail ?? update.error.problem.title) : undefined;

  const onSave = handleSubmit(async (input) => {
    await update.mutateAsync({ ...input, roleMappings });
    if (passwordDirty) await updateSecurity.mutateAsync({ ...security!, passwordSignInEnabled: passwordEnabled });
  });

  const callbackUrl = typeof window !== 'undefined' ? `${window.location.origin}/api/v1/auth/entra/callback` : '';
  const requireSso = (domains ?? []).some((d) => d.verifiedAt && d.ssoRequired);
  const verifiedDomains = (domains ?? []).filter((d) => d.verifiedAt);
  const unverifiedDomains = (domains ?? []).filter((d) => !d.verifiedAt);

  const addMapping = () => setRoleMappings((m) => [...m, { entraGroupId: '', roleId: roles?.[0]?.id ?? '' }]);
  const removeMapping = (i: number) => setRoleMappings((m) => m.filter((_, idx) => idx !== i));
  const updateMapping = (i: number, patch: Partial<EntraRoleMapping>) => setRoleMappings((m) => m.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const breadcrumb = [{ label: 'Administration', to: '/admin' }, { label: 'Authentication & SSO' }];

  if (isLoading || !data) return <PageHeader breadcrumb={breadcrumb} />;

  return (
    <>
      <PageHeader breadcrumb={breadcrumb} />
      <main className="relative flex flex-col gap-4 overflow-hidden px-8 py-7" style={{ height: 'calc(100vh - 60px)', flex: 'none' }}>
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em]">Authentication &amp; SSO</h1>
            <p className="text-sm leading-5 text-text-muted">Choose how people sign in to OES Workspace and connect your Microsoft Entra ID tenant.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="md" onClick={() => testConnection.mutate()} loading={testConnection.isPending}>
              Test connection
            </Button>
            <Button size="md" onClick={onSave} loading={isSubmitting} disabled={!dirty}>
              Save
            </Button>
          </div>
        </div>

        {errorMessage && (
          <div role="alert" className="rounded-control bg-danger-soft px-3 py-2 text-sm font-medium text-danger">
            {errorMessage}
          </div>
        )}
        {testConnection.data && (
          <div role="status" className={`rounded-control px-3 py-2 text-sm font-medium ${testConnection.data.ok ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger'}`}>
            {testConnection.data.message}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          <div className="flex flex-col gap-4">
            <section aria-label="Sign-in methods" className="grid grid-cols-[1fr_1fr_1.25fr] rounded-dialog border border-border bg-surface shadow-card">
              <div className="flex flex-col gap-1.5 border-r border-divider p-4.5">
                <div className="flex items-center gap-2.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="m3 7 9 6 9-6" />
                  </svg>
                  <span className="flex-1 text-sm font-semibold">Email + password</span>
                  <Switch checked={passwordEnabled} onChange={setPasswordEnabled} label="Email + password sign-in" hideLabel />
                </div>
                <span className="text-xs leading-4.25 text-text-muted">For users outside verified domains.</span>
              </div>
              <div className="flex flex-col gap-1.5 border-r border-divider p-4.5">
                <div className="flex items-center gap-2.5">
                  <MicrosoftLogo />
                  <span className="flex-1 text-sm font-semibold">Microsoft Entra ID</span>
                  <Switch checked={watch('enabled') ?? false} onChange={(v) => setValue('enabled', v, { shouldDirty: true })} label="Microsoft Entra ID sign-in" hideLabel />
                </div>
                <span className="text-xs leading-4.25 text-text-muted">Adds &ldquo;Sign in with Microsoft&rdquo; to login.</span>
              </div>
              <div className="flex flex-col gap-1.5 p-4.5">
                <div className="flex items-center gap-2.5">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 3 4 6v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V6z" />
                  </svg>
                  <span className="flex-1 text-sm font-semibold">Require SSO for verified domains</span>
                  <Switch checked={requireSso} onChange={(v) => setRequireSso.mutate(v)} disabled={verifiedDomains.length === 0} label="Require SSO for verified domains" hideLabel />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {verifiedDomains.map((d) => (
                    <span key={d.id} className="flex h-5.5 items-center gap-1 rounded-chip border border-divider px-2 font-mono text-xs">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" role="img" aria-label="Verified" aria-hidden="true">
                        <path d="m5 12 5 5 9-10" />
                      </svg>
                      {d.domain}
                      <button type="button" onClick={() => removeDomain.mutate(d.id)} aria-label={`Remove ${d.domain}`} className="text-text-muted hover:text-danger">
                        ×
                      </button>
                    </span>
                  ))}
                  {unverifiedDomains.map((d) => (
                    <span key={d.id} className="flex h-5.5 items-center gap-1 rounded-chip border border-dashed border-border-strong px-2 font-mono text-xs text-text-muted">
                      {d.domain}
                      <button type="button" onClick={() => verifyDomain.mutate(d.id)} className="font-semibold text-primary">
                        Verify
                      </button>
                    </span>
                  ))}
                  {showAddDomain ? (
                    <form
                      className="flex items-center gap-1"
                      onSubmit={async (e) => {
                        e.preventDefault();
                        if (!newDomain.trim()) return;
                        await addDomain.mutateAsync({ domain: newDomain.trim() });
                        setNewDomain('');
                        setShowAddDomain(false);
                      }}
                    >
                      <Input label="Domain" hideLabel value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="example.com" className="h-6 w-32 text-xs" />
                      <button type="submit" className="text-xs font-semibold text-primary">
                        Add
                      </button>
                    </form>
                  ) : (
                    <button type="button" onClick={() => setShowAddDomain(true)} className="text-xs font-semibold text-primary">
                      Add domain
                    </button>
                  )}
                </div>
              </div>
            </section>

            <div className="flex items-start gap-4">
              <section aria-labelledby="entra-h" className="min-w-0 flex-1 rounded-dialog border border-border bg-surface shadow-card">
                <header className="flex h-14 items-center gap-3 border-b border-divider px-4.5">
                  <span className="flex size-8 items-center justify-center rounded-control border border-border">
                    <MicrosoftLogo />
                  </span>
                  <div className="flex flex-1 flex-col leading-tight">
                    <h2 id="entra-h" className="text-[15px] font-semibold">
                      Microsoft Entra ID
                    </h2>
                    <span className="text-xs text-text-muted">OpenID Connect · PKCE</span>
                  </div>
                  {health?.lastSignInAt && <span className="text-xs text-text-muted">Last successful sign-in {timeAgo(health.lastSignInAt)}</span>}
                  <span className={`flex h-6 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold ${data.enabled && data.secretConfigured ? 'bg-success-soft text-success' : 'bg-surface-subtle text-text-secondary'}`}>
                    <span className="size-1.5 rounded-full" style={{ background: data.enabled && data.secretConfigured ? 'var(--color-success)' : 'var(--color-text-faint)' }} />
                    {data.enabled && data.secretConfigured ? 'Connected' : 'Not connected'}
                  </span>
                </header>

                <div className="flex flex-col gap-2.5 px-4.5 py-3">
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
                      Directory (tenant) ID
                      <span className="flex h-9 items-center gap-1.5 rounded-control border border-border-strong bg-surface pl-3 pr-1">
                        <input
                          type="text"
                          readOnly
                          value={showTenantId ? watch('entraTenantId') || '' : maskMiddle(watch('entraTenantId') || '')}
                          className="min-w-0 flex-1 bg-transparent font-mono text-xs"
                        />
                        <button type="button" aria-label="Show tenant ID" onClick={() => setShowTenantId((s) => !s)} className="flex size-7 items-center justify-center rounded-chip text-text-secondary hover:bg-surface-subtle">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                      </span>
                    </label>
                    <Input label="Application (client) ID" className="font-mono text-xs" {...register('clientId', { setValueAs: (v: string) => (v === '' ? null : v) })} />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <span className="text-[13px] font-semibold">Client secret</span>
                    <div className="flex h-9 items-center gap-2 rounded-control border border-divider bg-surface-subtle pl-3 pr-2">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="4" y="11" width="16" height="10" rx="2" />
                        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
                      </svg>
                      <span className="flex-1 text-[13px] text-text-secondary">{data.secretConfigured ? 'Stored securely in Doppler — rotate in Doppler' : 'Not set — add ENTRA_CLIENT_SECRET'}</span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <span className="text-[13px] font-semibold">
                      Redirect URI <span className="font-normal text-text-muted">— add this as a Web redirect in Entra</span>
                    </span>
                    <div className="flex gap-2">
                      <input type="text" readOnly value={callbackUrl} className="h-9 min-w-0 flex-1 rounded-control border border-divider bg-surface-subtle px-3 font-mono text-xs" />
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(callbackUrl)}
                        className="flex h-9 items-center gap-1.5 rounded-control border border-border-strong px-3 text-[13px] font-semibold"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 border-t border-divider pt-3">
                    <div role="radiogroup" aria-label="Allowed tenants" className="flex flex-col gap-1.5">
                      <span className="text-[13px] font-semibold">Allowed tenants</span>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="radio" checked={watch('allowedTenants') === 'single'} onChange={() => setValue('allowedTenants', 'single', { shouldDirty: true })} style={{ accentColor: 'var(--color-primary)' }} />
                        Single tenant <span className="text-xs text-text-muted">This organisation only</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="radio" checked={watch('allowedTenants') === 'organizations'} onChange={() => setValue('allowedTenants', 'organizations', { shouldDirty: true })} style={{ accentColor: 'var(--color-primary)' }} />
                        Multi-tenant (SaaS)
                      </label>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <div className="flex items-center gap-2.5">
                        <span className="flex-1 text-[13px] font-semibold">Just-in-time provisioning</span>
                        <Switch checked={watch('jitEnabled') ?? false} onChange={(v) => setValue('jitEnabled', v, { shouldDirty: true })} label="Just-in-time provisioning" hideLabel />
                      </div>
                      {watch('jitEnabled') && (
                        <>
                          <span className="text-xs text-text-muted">Default role for new SSO users</span>
                          <Select
                            label="Default role"
                            hideLabel
                            value={watch('defaultRoleId') ?? ''}
                            onChange={(e) => setValue('defaultRoleId', e.target.value || null, { shouldDirty: true })}
                            options={[{ value: '', label: 'No role (assign manually later)' }, ...(roles ?? []).map((r) => ({ value: r.id, label: r.name }))]}
                          />
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <span className="flex items-baseline gap-2">
                      <span className="text-[13px] font-semibold">Group → role mapping</span>
                      <span className="text-xs text-text-muted">Evaluated when a new account is provisioned; highest role wins.</span>
                    </span>
                    <div className="overflow-hidden rounded-control border border-border">
                      <div className="grid h-6.5 items-center gap-1 bg-surface-subtle px-3 text-xs font-semibold text-text-muted" style={{ gridTemplateColumns: '1.3fr 24px 1fr 40px' }}>
                        <span>Entra group</span>
                        <span />
                        <span>Orion role</span>
                        <span />
                      </div>
                      {roleMappings.map((m, i) => (
                        <div key={i} className="grid h-8.5 items-center gap-1 border-t border-divider px-3 text-[13px]" style={{ gridTemplateColumns: '1.3fr 24px 1fr 40px' }}>
                          <input
                            value={m.entraGroupId}
                            onChange={(e) => updateMapping(i, { entraGroupId: e.target.value })}
                            placeholder="CRM-Admins"
                            className="h-6.5 min-w-0 rounded-xs border border-transparent bg-transparent font-mono text-xs hover:border-border-strong focus:border-border-strong"
                          />
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-text-faint">
                            <path d="M5 12h14M13 6l6 6-6 6" />
                          </svg>
                          <select value={m.roleId} onChange={(e) => updateMapping(i, { roleId: e.target.value })} className="h-6.5 rounded-xs border border-transparent bg-transparent text-xs">
                            {(roles ?? []).map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name}
                              </option>
                            ))}
                          </select>
                          <button type="button" aria-label={`Remove mapping ${m.entraGroupId}`} onClick={() => removeMapping(i)} className="flex justify-center text-text-secondary hover:text-danger">
                            ×
                          </button>
                        </div>
                      ))}
                      <button type="button" onClick={addMapping} className="flex h-7.5 w-full items-center gap-1.5 px-3 text-[13px] font-semibold text-primary">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                        Add mapping
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 border-t border-divider pt-3">
                    <div className="flex flex-1 flex-col gap-0.5">
                      <span className="text-[13px] font-semibold">Account linking</span>
                      <span className="text-xs text-text-muted">Link SSO identities to existing users by verified email</span>
                    </div>
                    <Switch checked={watch('linkByVerifiedEmail') ?? false} onChange={(v) => setValue('linkByVerifiedEmail', v, { shouldDirty: true })} label="Account linking" hideLabel />
                  </div>
                </div>
              </section>

              <aside className="flex w-71 shrink-0 flex-col gap-3">
                <section aria-labelledby="guide-h" className="flex flex-col gap-3 rounded-dialog border border-border bg-surface p-4.5 shadow-card">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Setup guide</span>
                    <h2 id="guide-h" className="text-[15px] font-semibold leading-5">
                      Register the app in Entra admin center
                    </h2>
                  </div>
                  <ol className="flex flex-col gap-2.5 text-[13px] leading-4.5 text-text-secondary">
                    <li className="flex gap-2.5">
                      <StepBadge n={1} />
                      <span>
                        In <strong className="font-semibold text-ink">App registrations</strong>, choose New registration and name it &ldquo;Orion CRM&rdquo;.
                      </span>
                    </li>
                    <li className="flex gap-2.5">
                      <StepBadge n={2} />
                      <span>Pick &ldquo;This organizational directory only&rdquo; and paste the Redirect URI as a Web platform.</span>
                    </li>
                    <li className="flex gap-2.5">
                      <StepBadge n={3} />
                      <span>
                        Under <strong className="font-semibold text-ink">Certificates &amp; secrets</strong>, create a secret and store it in Doppler — never paste it here.
                      </span>
                    </li>
                    <li className="flex gap-2.5">
                      <StepBadge n={4} />
                      <span>Add a groups claim, grant admin consent, then copy the tenant and client IDs here.</span>
                    </li>
                  </ol>
                  <a
                    href="https://entra.microsoft.com"
                    target="_blank"
                    rel="noreferrer"
                    className="flex h-8 items-center justify-center gap-1.5 rounded-chip border border-border-strong text-[13px] font-semibold"
                  >
                    Open Entra admin center
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M14 4h6v6M20 4l-9 9" />
                      <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
                    </svg>
                  </a>
                </section>

                <section aria-labelledby="health-h" className="flex flex-col gap-2.5 rounded-dialog border border-border bg-surface p-4.5 shadow-card">
                  <h2 id="health-h" className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                    Connection health
                  </h2>
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-text-secondary">Last test</span>
                    {data.lastTestAt ? (
                      <span className={`flex items-center gap-1.5 font-semibold ${data.lastTestOk ? 'text-success' : 'text-danger'}`}>
                        <span className="size-1.5 rounded-full" style={{ background: data.lastTestOk ? 'var(--color-success)' : 'var(--color-danger)' }} />
                        {data.lastTestOk ? 'Passed' : 'Failed'} · {formatDate(data.lastTestAt)}
                      </span>
                    ) : (
                      <span className="text-text-muted">Never tested</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-text-secondary">SSO sign-ins (24 h)</span>
                    <span className="font-semibold tabular-nums">{health?.signInsLast24h ?? '—'}</span>
                  </div>
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-text-secondary">Failed (24 h)</span>
                    <span className="font-semibold tabular-nums">{health?.failedLast24h ?? '—'}</span>
                  </div>
                  <div className="flex items-center justify-between text-[13px]">
                    <span className="text-text-secondary">Users linked to Entra</span>
                    <span className="font-semibold tabular-nums">
                      {health ? `${health.usersLinked} of ${health.totalActiveUsers}` : '—'}
                    </span>
                  </div>
                </section>
              </aside>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

function StepBadge({ n }: { n: number }) {
  return (
    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-success-soft text-[11px] font-bold text-success">{n}</span>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <rect x="2" y="2" width="9.5" height="9.5" fill="#F25022" />
      <rect x="12.5" y="2" width="9.5" height="9.5" fill="#7FBA00" />
      <rect x="2" y="12.5" width="9.5" height="9.5" fill="#00A4EF" />
      <rect x="12.5" y="12.5" width="9.5" height="9.5" fill="#FFB900" />
    </svg>
  );
}
